import { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, Save, X, DollarSign, Settings, Users, TrendingUp, Zap, RotateCcw } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface CreditSettings {
  id: string;
  chatCreditCost: number;
  generalCreditCost: number;
  imageCreditCost: number;
  extraCreditPrice: number;
  discount50Credits: number;
  discount100Credits: number;
  discount200Credits: number;
  minExtraCredits: number;
  maxExtraCredits: number;
  lowCreditThreshold: number;
  freeTierMonthlyCredits: number;
}

interface FeatureCostInfo {
  cost: number;
  isCustom: boolean;
  feature: string;
  label: string;
  category: string;
}

interface FeatureCostsData {
  featureCosts: Record<string, FeatureCostInfo>;
  categoryDefaults: {
    chat: number;
    general: number;
    image: number;
  };
}

interface SubscriptionPlan {
  id: string;
  tier: string;
  name: string;
  monthlyCredits: number;
  price: number;
  stripePriceId: string | null;
  features: string[];
  isPopular: boolean;
  isBestValue: boolean;
  isActive: boolean;
}

interface UserSubscription {
  userId: string;
  user: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  tier: string;
  monthlyCredits: number;
  creditsRemaining: number;
  totalCreditsConsumed: number;
  totalCreditsAdded: number;
  currentPeriodEnd: string | null;
  status: string;
}

interface CreditStats {
  totalUsers: number;
  freeUsers: number;
  basicUsers: number;
  premiumUsers: number;
  unlimitedUsers: number;
  totalCreditsConsumed: number;
  totalCreditsAdded: number;
  totalRevenue: number;
}

interface Props {
  accessToken: string;
}

export default function AdminCreditsManagement({ accessToken }: Props) {
  const [settings, setSettings] = useState<CreditSettings | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [stats, setStats] = useState<CreditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [tempSettings, setTempSettings] = useState<CreditSettings | null>(null);
  const [tempPlan, setTempPlan] = useState<SubscriptionPlan | null>(null);
  
  // Per-feature costs state
  const [featureCostsData, setFeatureCostsData] = useState<FeatureCostsData | null>(null);
  const [editingFeatureCosts, setEditingFeatureCosts] = useState(false);
  const [tempFeatureCosts, setTempFeatureCosts] = useState<Record<string, string>>({});
  const [tempCategoryDefaults, setTempCategoryDefaults] = useState<{ chat: string; general: string; image: string }>({ chat: '', general: '', image: '' });
  const [savingFeatureCosts, setSavingFeatureCosts] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, plansRes, usersRes, statsRes, featureCostsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/credits/settings`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${API_URL}/api/admin/subscriptions/plans`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${API_URL}/api/admin/subscriptions/users`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${API_URL}/api/admin/subscriptions/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${API_URL}/api/admin/credits/feature-costs`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }).catch(() => ({ data: null }))
      ]);

      setSettings(settingsRes.data);
      setPlans(plansRes.data.plans || []);
      setUserSubscriptions(usersRes.data.subscriptions || []);
      setStats(statsRes.data);
      
      if (featureCostsRes.data) {
        setFeatureCostsData(featureCostsRes.data);
      }
    } catch (error: any) {
      console.error('Error fetching credit data:', error);
      alert('Eroare la încărcarea datelor: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!tempSettings) return;
    
    try {
      await axios.patch(
        `${API_URL}/api/admin/credits/settings`,
        tempSettings,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('✅ Setări salvate cu succes!');
      setSettings(tempSettings);
      setEditingSettings(false);
      fetchData();
    } catch (error: any) {
      alert('Eroare la salvare: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSavePlan = async () => {
    if (!tempPlan) return;
    
    try {
      await axios.patch(
        `${API_URL}/api/admin/subscriptions/plans/${tempPlan.id}`,
        {
          name: tempPlan.name,
          monthlyCredits: tempPlan.monthlyCredits,
          price: tempPlan.price,
          stripePriceId: tempPlan.stripePriceId,
          features: tempPlan.features,
          isPopular: tempPlan.isPopular,
          isBestValue: tempPlan.isBestValue,
          isActive: tempPlan.isActive
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('✅ Plan salvat cu succes!');
      setEditingPlan(null);
      fetchData();
    } catch (error: any) {
      alert('Eroare la salvare: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAdjustCredits = async (userId: string, email: string) => {
    const amount = prompt(`Ajustare credite pentru ${email}\n\nIntroduceți număr (pozitiv = adaugă, negativ = scoate):`);
    if (!amount) return;
    
    const numAmount = parseInt(amount);
    if (isNaN(numAmount)) {
      alert('Număr invalid!');
      return;
    }

    const reason = prompt('Motiv pentru ajustare:');
    if (!reason) return;

    try {
      await axios.post(
        `${API_URL}/api/admin/credits/adjust`,
        { userId, amount: numAmount, reason },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert(`✅ Ajustare efectuată cu succes! ${numAmount > 0 ? '+' : ''}${numAmount} credite`);
      fetchData();
    } catch (error: any) {
      alert('Eroare: ' + (error.response?.data?.error || error.message));
    }
  };

  // === Per-Feature Costs Handlers ===
  const startEditingFeatureCosts = () => {
    if (!featureCostsData) return;
    const temp: Record<string, string> = {};
    for (const [feature, info] of Object.entries(featureCostsData.featureCosts)) {
      temp[feature] = info.isCustom ? String(info.cost) : '';
    }
    setTempFeatureCosts(temp);
    setTempCategoryDefaults({
      chat: String(featureCostsData.categoryDefaults.chat),
      general: String(featureCostsData.categoryDefaults.general),
      image: String(featureCostsData.categoryDefaults.image),
    });
    setEditingFeatureCosts(true);
  };

  const handleSaveFeatureCosts = async () => {
    try {
      setSavingFeatureCosts(true);
      
      // 1. Save category defaults first (via settings endpoint)
      const chatVal = parseFloat(tempCategoryDefaults.chat);
      const generalVal = parseFloat(tempCategoryDefaults.general);
      const imageVal = parseFloat(tempCategoryDefaults.image);
      
      if (!isNaN(chatVal) && !isNaN(generalVal) && !isNaN(imageVal)) {
        await axios.patch(
          `${API_URL}/api/admin/credits/settings`,
          {
            chatCreditCost: chatVal,
            generalCreditCost: generalVal,
            imageCreditCost: imageVal,
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }

      // 2. Save per-feature costs
      const featureCosts: Record<string, number | null> = {};
      for (const [feature, value] of Object.entries(tempFeatureCosts)) {
        featureCosts[feature] = value === '' ? null : parseFloat(value);
      }

      await axios.patch(
        `${API_URL}/api/admin/credits/feature-costs`,
        { featureCosts },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      alert('✅ Costuri per modul salvate cu succes!');
      setEditingFeatureCosts(false);
      fetchData();
    } catch (error: any) {
      alert('Eroare la salvare: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingFeatureCosts(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'CHAT': return '💬';
      case 'IMAGE': return '📷';
      default: return '⚡';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'CHAT': return 'bg-blue-100 text-blue-700';
      case 'IMAGE': return 'bg-purple-100 text-purple-700';
      default: return 'bg-orange-100 text-orange-700';
    }
  };

  const getCategoryDefault = (category: string) => {
    if (!featureCostsData) return 0;
    switch (category) {
      case 'CHAT': return featureCostsData.categoryDefaults.chat;
      case 'IMAGE': return featureCostsData.categoryDefaults.image;
      default: return featureCostsData.categoryDefaults.general;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 opacity-80" />
              <div className="text-right">
                <div className="text-2xl font-bold">{stats.totalUsers}</div>
                <div className="text-sm opacity-90">Total Users</div>
              </div>
            </div>
            <div className="text-xs mt-3 pt-3 border-t border-white/20">
              FREE: {stats.freeUsers} | BASIC: {stats.basicUsers} | PREMIUM: {stats.premiumUsers} | UNLIMITED: {stats.unlimitedUsers}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div className="text-right">
                <div className="text-2xl font-bold">{stats.totalCreditsAdded.toLocaleString()}</div>
                <div className="text-sm opacity-90">Credite Adăugate</div>
              </div>
            </div>
            <div className="text-xs mt-3">Total credite distribuite</div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Settings className="w-8 h-8 opacity-80" />
              <div className="text-right">
                <div className="text-2xl font-bold">{stats.totalCreditsConsumed.toLocaleString()}</div>
                <div className="text-sm opacity-90">Credite Consumate</div>
              </div>
            </div>
            <div className="text-xs mt-3">Utilizare totală AI</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 opacity-80" />
              <div className="text-right">
                <div className="text-2xl font-bold">€{stats.totalRevenue.toFixed(2)}</div>
                <div className="text-sm opacity-90">Revenue Total</div>
              </div>
            </div>
            <div className="text-xs mt-3">Venituri subscriptions + extra</div>
          </div>
        </div>
      )}

      {/* Credit Settings */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">⚙️ Setări Credite</h2>
            <p className="text-sm text-gray-600 mt-1">Configurează costurile și discounturile</p>
          </div>
          {!editingSettings ? (
            <button
              onClick={() => {
                setTempSettings(settings);
                setEditingSettings(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit className="w-4 h-4" />
              Editează
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSaveSettings}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Save className="w-4 h-4" />
                Salvează
              </button>
              <button
                onClick={() => {
                  setEditingSettings(false);
                  setTempSettings(null);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                <X className="w-4 h-4" />
                Anulează
              </button>
            </div>
          )}
        </div>

        {settings && (
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Extra Credits Pricing */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">💳 Prețuri Extra Credite</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preț per credit (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingSettings ? tempSettings?.extraCreditPrice : settings.extraCreditPrice}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, extraCreditPrice: parseFloat(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount 50+ credite (%)</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.discount50Credits : settings.discount50Credits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, discount50Credits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount 100+ credite (%)</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.discount100Credits : settings.discount100Credits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, discount100Credits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount 200+ credite (%)</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.discount200Credits : settings.discount200Credits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, discount200Credits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
              </div>

              {/* Limits & Thresholds */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">📊 Limite & Praguri</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minim extra credite</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.minExtraCredits : settings.minExtraCredits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, minExtraCredits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maxim extra credite</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.maxExtraCredits : settings.maxExtraCredits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, maxExtraCredits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prag warning credite mici (%)</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.lowCreditThreshold : settings.lowCreditThreshold}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, lowCreditThreshold: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credite FREE tier lunar</label>
                  <input
                    type="number"
                    value={editingSettings ? tempSettings?.freeTierMonthlyCredits : settings.freeTierMonthlyCredits}
                    onChange={(e) => tempSettings && setTempSettings({...tempSettings, freeTierMonthlyCredits: parseInt(e.target.value)})}
                    disabled={!editingSettings}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* PER-FEATURE CREDIT COSTS - NEW SECTION */}
      {/* ============================================ */}
      {featureCostsData && (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">⚡ Costuri per Modul / Solicitare</h2>
              <p className="text-sm text-gray-600 mt-1">
                Setează câte credite costă fiecare tip de solicitare AI. Lasă gol pentru a folosi costul implicit din categorie.
              </p>
            </div>
            {!editingFeatureCosts ? (
              <button
                onClick={startEditingFeatureCosts}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                <Edit className="w-4 h-4" />
                Editează
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveFeatureCosts}
                  disabled={savingFeatureCosts}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingFeatureCosts ? 'Se salvează...' : 'Salvează'}
                </button>
                <button
                  onClick={() => {
                    setEditingFeatureCosts(false);
                    setTempFeatureCosts({});
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  <X className="w-4 h-4" />
                  Anulează
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Category defaults - editable */}
            <div className={`mb-6 p-4 rounded-lg border ${editingFeatureCosts ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">📋 Costuri implicite per categorie (fallback):</h4>
              {editingFeatureCosts ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-blue-700 mb-1">
                      <span className="px-2 py-0.5 bg-blue-100 rounded">💬 CHAT</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={tempCategoryDefaults.chat}
                      onChange={(e) => setTempCategoryDefaults({...tempCategoryDefaults, chat: e.target.value})}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Credite per mesaj chat</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-orange-700 mb-1">
                      <span className="px-2 py-0.5 bg-orange-100 rounded">⚡ GENERAL</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={tempCategoryDefaults.general}
                      onChange={(e) => setTempCategoryDefaults({...tempCategoryDefaults, general: e.target.value})}
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Credite per generare AI</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-purple-700 mb-1">
                      <span className="px-2 py-0.5 bg-purple-100 rounded">📷 IMAGE</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={tempCategoryDefaults.image}
                      onChange={(e) => setTempCategoryDefaults({...tempCategoryDefaults, image: e.target.value})}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Credite per analiză imagine</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-6 text-sm">
                  <span className="flex items-center gap-1">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">💬 CHAT</span>
                    <span className="font-bold text-lg">{featureCostsData.categoryDefaults.chat}</span> credite
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">⚡ GENERAL</span>
                    <span className="font-bold text-lg">{featureCostsData.categoryDefaults.general}</span> credite
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">📷 IMAGE</span>
                    <span className="font-bold text-lg">{featureCostsData.categoryDefaults.image}</span> credite
                  </span>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                {editingFeatureCosts 
                  ? 'Acestea sunt costurile implicite. Modulele fără cost personalizat vor folosi aceste valori.'
                  : 'Dacă un modul nu are cost personalizat setat, se va folosi costul categoriei sale.'
                }
              </p>
            </div>

            {/* Feature costs grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(featureCostsData.featureCosts).map(([feature, info]) => {
                const isEditing = editingFeatureCosts;
                const currentValue = isEditing ? tempFeatureCosts[feature] : (info.isCustom ? String(info.cost) : '');
                const effectiveCost = isEditing
                  ? (tempFeatureCosts[feature] === '' ? getCategoryDefault(info.category) : parseInt(tempFeatureCosts[feature]) || 0)
                  : info.cost;

                return (
                  <div
                    key={feature}
                    className={`border rounded-xl p-4 transition-all ${
                      isEditing ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'
                    } ${info.isCustom && !isEditing ? 'border-amber-200 bg-amber-50/20' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{getCategoryIcon(info.category)}</span>
                          <span className="font-semibold text-gray-900 text-sm">{info.label}</span>
                        </div>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(info.category)}`}>
                          {info.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">{effectiveCost}</div>
                        <div className="text-xs text-gray-500">credite</div>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="mt-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            value={currentValue}
                            onChange={(e) => setTempFeatureCosts({
                              ...tempFeatureCosts,
                              [feature]: e.target.value
                            })}
                            placeholder={`${getCategoryDefault(info.category)} (implicit)`}
                            className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                          {currentValue !== '' && (
                            <button
                              onClick={() => setTempFeatureCosts({
                                ...tempFeatureCosts,
                                [feature]: ''
                              })}
                              title="Resetează la implicit"
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {currentValue === '' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Folosește implicit: {getCategoryDefault(info.category)} credite ({info.category})
                          </p>
                        )}
                        {currentValue !== '' && (
                          <p className="text-xs text-amber-600 mt-1 font-medium">
                            ✨ Cost personalizat: {currentValue} credite
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        {info.isCustom ? (
                          <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            Cost personalizat
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">
                            Folosește implicit ({info.category}: {getCategoryDefault(info.category)})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <h2 className="text-xl font-bold text-gray-900">📦 Planuri de Abonament</h2>
          <p className="text-sm text-gray-600 mt-1">Editează prețuri, credite și features</p>
        </div>

        <div className="p-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => (
            <div key={plan.id} className={`border-2 rounded-xl p-4 ${
              editingPlan === plan.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-lg">{plan.tier}</h3>
                  {plan.isPopular && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">⭐ Popular</span>}
                  {plan.isBestValue && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full ml-1">🔥 Best Value</span>}
                </div>
                {editingPlan !== plan.id ? (
                  <button
                    onClick={() => {
                      setTempPlan(plan);
                      setEditingPlan(plan.id);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex gap-1">
                    <button
                      onClick={handleSavePlan}
                      className="text-green-600 hover:text-green-800"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingPlan(null);
                        setTempPlan(null);
                      }}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-600">Nume</label>
                  <input
                    type="text"
                    value={editingPlan === plan.id ? tempPlan?.name : plan.name}
                    onChange={(e) => tempPlan && setTempPlan({...tempPlan, name: e.target.value})}
                    disabled={editingPlan !== plan.id}
                    className="w-full px-2 py-1 text-sm border rounded disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Credite/lună</label>
                  <input
                    type="number"
                    value={editingPlan === plan.id ? tempPlan?.monthlyCredits : plan.monthlyCredits}
                    onChange={(e) => tempPlan && setTempPlan({...tempPlan, monthlyCredits: parseInt(e.target.value)})}
                    disabled={editingPlan !== plan.id}
                    className="w-full px-2 py-1 text-sm border rounded disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Preț (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingPlan === plan.id ? tempPlan?.price : plan.price}
                    onChange={(e) => tempPlan && setTempPlan({...tempPlan, price: parseFloat(e.target.value)})}
                    disabled={editingPlan !== plan.id || plan.tier === 'FREE'}
                    className="w-full px-2 py-1 text-sm border rounded disabled:bg-gray-50"
                  />
                </div>
                {plan.tier !== 'FREE' && (
                  <div>
                    <label className="text-xs text-gray-600">Stripe Price ID</label>
                    <input
                      type="text"
                      value={editingPlan === plan.id ? tempPlan?.stripePriceId || '' : plan.stripePriceId || ''}
                      onChange={(e) => tempPlan && setTempPlan({...tempPlan, stripePriceId: e.target.value})}
                      disabled={editingPlan !== plan.id}
                      placeholder="price_xxxxx"
                      className="w-full px-2 py-1 text-xs border rounded disabled:bg-gray-50"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={editingPlan === plan.id ? tempPlan?.isActive : plan.isActive}
                    onChange={(e) => tempPlan && setTempPlan({...tempPlan, isActive: e.target.checked})}
                    disabled={editingPlan !== plan.id}
                    className="rounded"
                  />
                  <span>Activ</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Subscriptions */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-teal-50">
          <h2 className="text-xl font-bold text-gray-900">👥 Utilizatori & Credite</h2>
          <p className="text-sm text-gray-600 mt-1">Vezi și ajustează creditele utilizatorilor</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilizator</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credite Rămase</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credite/Lună</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Consumate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resetare</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {userSubscriptions.map((sub) => {
                const percentage = Math.round((sub.creditsRemaining / sub.monthlyCredits) * 100);
                const isLow = percentage < 20;
                
                return (
                  <tr key={sub.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {sub.user.firstName} {sub.user.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{sub.user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        sub.tier === 'UNLIMITED' ? 'bg-purple-100 text-purple-700' :
                        sub.tier === 'PREMIUM' ? 'bg-blue-100 text-blue-700' :
                        sub.tier === 'BASIC' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>
                          {sub.creditsRemaining}
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                          <div
                            className={`h-2 rounded-full ${
                              isLow ? 'bg-red-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{percentage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sub.monthlyCredits}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{sub.totalCreditsConsumed}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString('ro-RO') : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleAdjustCredits(sub.userId, sub.user.email)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Ajustează
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
